import { useState, useEffect } from 'react';
import { MapCase, PdbImportCase, AlphaFoldImportCase } from '../../../../../model/MapCase';
import { Loader } from '../../../../../Loader';
import SelectSearch from '../../../../common/SelectSearch';
import { ImportErrors } from '../../import_components';

function SelectProteinTab({
    initData, selectProtein, connection, errors, selected,
}) {
    const dataConnection = connection;
    const mapList = dataConnection.getCaseDataCollection().getMapList();
    // Initialize the selector with just the subset that is likely to be visible on form opening.
    // The list gets fully populated by the useEffect; the only subtle change is the scrollbar size.
    // To make it open even faster, the call to prepareItems could be moved into the setTimeout.
    // However, this would mean we don't have the exact contents ahead of time, and the listbox
    // will probably flash.
    // prepareItems is ~O(n^2) over the full map list and is only needed for this initial slice
    // (the effect below repopulates the full list). Use the lazy useState initializer so it runs
    // once on mount rather than on every render of this always-mounted tab.
    const [availableProteins, setAvailableProteins] = useState(
        () => prepareItems(mapList).slice(0, 40),
    );
    const [query, setQuery] = useState(initData.options?.searchSeed || '');

    useEffect(() => {
        // This setTimeout allows the form to open before the data is 100% ready,
        // which makes it feel like less of a delay. Previously, it could take 1 sec to open.
        setTimeout(() => {
            const proteins = prepareItems(connection.getCaseDataCollection().getMapList());
            setAvailableProteins(checkForDynamicEntries(query, proteins));
        }, 0);
    }, [connection]);

    async function addProtein(selectedProtein) {
        const foundMapCase = availableProteins.find(
            (protein) => protein.id === selectedProtein.id
        )?.mapCase;
        if (foundMapCase) {
            await selectProtein(foundMapCase);
        }
    }

    // ⚠️ RETURNS THE SAME ARRAY WHEN NOTHING CHANGED, and that identity is load-bearing.
    // This runs on every keystroke. It used to `filter` unconditionally, so it handed back a
    // fresh array each time even when the contents were identical -- which is a new
    // `optionList` prop for SelectSearch on every character. That defeats the memo that
    // makes SelectSearch's deferred render cheap (see OptionRows there): the rows got
    // rebuilt on the urgent half of the keystroke, which is exactly the 14.5k-row render
    // the deferral exists to get out of the way. Measured, the cold first keystroke went
    // 389ms -> 1,553ms from this alone.
    // Returning the identical array also lets React bail out of the setState entirely.
    function checkForDynamicEntries(searchTerm, proteinList=availableProteins) {
        // If user entered a pbdid or alphafoldID then add a option to import it.
        const term = searchTerm.toUpperCase().trim();
        // Only rebuild if there is actually a temporary entry to drop.
        const newAvailableProteins = proteinList.some((protein) => protein.temporary)
            ? proteinList.filter((protein) => !protein.temporary)
            : proteinList;
        if (connection.connector.staticMode) {
            return newAvailableProteins;
        }
        // ⚠️ Collected, not pushed. `newAvailableProteins` may BE the caller's array now, so
        // pushing would mutate the live option list in place.
        const dynamicEntries = [];
        if (PdbImportCase.isValidId(term)) {
            const { description, itemName } = PdbImportCase.newRecordMsgInfo(term);
            dynamicEntries.push({
                id: term,
                pbdid: term,
                itemName,
                text: `${itemName}${itemName && description ? ': ' : ''}${description}`,
                linkInfo: PdbImportCase.externalLinkInfo(term),
                mapCase: new PdbImportCase(term),
                temporary: true,
                groupName: 'Import from PDB',
                title: 'Import the raw structure from the Protein Data Bank',
                searchTerms: [term],
            });
        }
        if (AlphaFoldImportCase.isValidId(term)) {
            const { description, itemName } = AlphaFoldImportCase.newRecordMsgInfo(term);
            dynamicEntries.push({
                id: term,
                itemName,
                text: `${itemName}${itemName && description ? ': ' : ''}${description}`,
                linkInfo: AlphaFoldImportCase.externalLinkInfo(term),
                mapCase: new AlphaFoldImportCase(term),
                temporary: true,
                groupName: 'Import from AlphaFold',
                title: 'Import the predicted structure from the AlphaFold database',
                searchTerms: [term],
            });
        }
        if (dynamicEntries.length === 0) {
            return newAvailableProteins;
        }
        return [...newAvailableProteins, ...dynamicEntries];
    }

    return (
        <SelectSearch
            optionList={availableProteins}
            onSelection={addProtein}
            label="Type a protein name, protein class, or PDB ID (may be DNA/RNA)"
            hasLinks
            focusWhenSelected={selected}
            defaultQuery={query}
            checkForDynamicEntries={(searchTerm) => {
                setQuery(searchTerm);
                setAvailableProteins(checkForDynamicEntries(searchTerm));
            }}
            error={errors && <ImportErrors errors={errors} />}
        />
    );
}
export default SelectProteinTab;

// Group the map list into the contiguous blocks the picker shows, in ONE pass.
//
// This used to build the list by searching it: `findLastIndex` for the group's last member
// and `splice` to insert after it, per entry -- O(n^2) over the full map list. Collecting
// each group's members into a Map and concatenating the blocks at the end is the same
// arrangement for one scan.
//
// ⚠️ ON TODAY'S DATA THIS IS NOT THE LAG, and the measurement is worth recording so nobody
// re-fixes it expecting one. Nearly all 11.8k structures sit in ONE group (the pre-aligned
// kinase selectivity library), and `findLastIndex` scans from the END, so it matched on its
// first comparison every time: 5ms for the old version against 4ms for this one. The
// quadratic term is LATENT -- it needs groups interleaved, which is what a map list with
// many projects looks like. Synthetic 12k entries round-robin over 60 groups: 654ms -> 3ms.
// The picker's actual delay is REACT RENDERING every matching row (14,550 options = 72,913
// DOM nodes, ~1.5s per broad keystroke); see e2e/tests/structure-picker-perf.spec.js.
//
// ⚠️ THE BLOCK ORDER IS NOT ALPHABETICAL AND NOT FIRST-SEEN. It is reproduced here exactly
// as the splice version produced it, quirk included: a newly seen TOP group was `unshift`ed
// to the front, so top-group blocks come out in REVERSE order of first appearance, ahead of
// every other group; other groups keep first-appearance order. A group's displayed name is
// the casing of its first-seen member (groups are matched case-insensitively).
function prepareItems(mapCaseList) {
    const topGroups = [
        MapCase.GroupNames.PdbImports,
        MapCase.GroupNames.UserUploads,
        MapCase.GroupNames.AlphaFoldImports,
        MapCase.GroupNames.UserFolded,
    ].map((s) => s.toLowerCase());

    /** @type {Map<string, {groupName: string, items: object[]}>} */
    const blocks = new Map();
    const topOrder = [];
    const otherOrder = [];

    mapCaseList.forEach((mapCase, index) => {
        const { itemName, description, title } = displayParts(mapCase);
        const groupName = mapCase.group_name;
        const groupNameLowerCase = groupName.toLowerCase();
        const text = `${itemName}${itemName && description && ':  '}${description}`;

        let block = blocks.get(groupNameLowerCase);
        if (!block) {
            block = { groupName, items: [] };
            blocks.set(groupNameLowerCase, block);
            (topGroups.includes(groupNameLowerCase) ? topOrder : otherOrder)
                .push(groupNameLowerCase);
        }

        block.items.push({
            // The first-seen casing, which every later member of the group adopted when the
            // splice version copied it off the item it inserted after.
            groupName: block.groupName,
            title,
            id: index,
            linkInfo: mapCase.myExternalLinkInfo(),
            text,
            mapCase,
            // Non-human organism is searchable via `title` ("Organism: <x>", set in
            // displayParts) and `text` (the description carries it) -- both shown to the
            // user. Human is the unmarked default, so it is neither shown nor searchable,
            // keeping search consistent with the display (no phantom "human" matches).
            searchTerms: [text, groupName, title, mapCase.project],
        });
    });

    return [...topOrder].reverse().concat(otherOrder)
        .flatMap((key) => blocks.get(key).items);
}

function displayParts(mapCase) {
    let itemName = mapCase.pdbID || mapCase.molecule_name || mapCase.gene_name;
    let description = mapCase.description;
    let source = '';

    switch (mapCase.sourceDesc) {
        case MapCase.Sources.Public:
        case MapCase.Sources.Private:
            // "Prepared' prefix has two purposes:
            // 1) distinguish cases on which we have done full optimization and simulation
            // 2) Avoid the appearance of using pdb id to refer to our prepared data
            // That is, the referent of a pdb id should be the data in the pdb, not our data.
            // See: ftp://ftp.wwpdb.org/pub/pdb/advisory.txt
            itemName = mapCase.pdbID ? `Prepared from ${mapCase.pdbID}` : 'Prepared structure';
            source = mapCase?.dataSource.name;
            break;
        case MapCase.Sources.Fragserv: {
            // For user structures, we want to display the full specified project/case names.
            // But if the project name matches the molecule name, it will already be in the
            // map selector group header, so just use the case.
            const displayName = mapCase.project === mapCase.molecule_name.replace(/ /g, '-')
                ? mapCase.case
                : mapCase.projectCase;
            itemName = `User data ${displayName}`;
            source = mapCase?.dataSource.name || 'Custom simulations';
            break;
        }
        case MapCase.Sources.PDB:
            itemName = mapCase.pdbID;
            source = 'Imported from the Protein Data Bank';
            break;
        case MapCase.Sources.ImportScreen:
            source = 'Imported by user';
            break;
        case MapCase.Sources.AlphaFold:
            source = 'Imported from the AlphaFold database';
            break;
        // no default - itemName assigned above
    }

    let title = source ? `Source: ${source}` : '';
    if (mapCase.projectCase && Loader.AllowLabFeatures) {
        const prefix = title ? '. ' : '';
        title += `${prefix}Project Code: ${mapCase.projectCase}`;
    }
    if (mapCase?.lastUpdate) {
        const prefix = title ? '. ' : '';
        title += `${prefix}Last update: ${mapCase.lastUpdate.toLocaleDateString()}`;
    }

    // Intent: if we have pdbid but no description, show mol or gene name for desc in map selector
    if (!description
        && itemName !== mapCase.molecule_name && itemName !== mapCase.gene_name) {
        description = mapCase.molecule_name || mapCase.gene_name;
    }

    // The picker item's tooltip always names the organism (Human included), so it is
    // consistently searchable (via `title` in searchTerms) and any match -- "human"
    // included -- is explained on hover. The visible label stays non-human-only (the
    // map-list description; see prep-map-list.py), so the row itself isn't cluttered.
    if (mapCase?.organism) {
        const prefix = title ? '. ' : '';
        title += `${prefix}Organism: ${mapCase.organism}`;
    }

    return { itemName, description, title };
}
